import { Container } from '@almbrand/container';
import { FormWrapper } from '@almbrand/formwrapper';
import { Headline } from '@almbrand/headline';
import { useDeviceQuery } from '@almbrand/usedevicequery';
import classNames from 'classnames';
import { LoginButtonComponent, ModalComponent, StepComponent } from 'components';
import { Component } from 'components/Component';
import { IsPackageComponentContext } from 'context/IsPackageComponentContext';
import { useFormSubmit } from 'hooks';
import { memo, useContext, useEffect, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useAppSelector } from 'store/hooks';
import { getComponentProperties } from '../../../services/MapFieldService';
import { isPackageStep } from '../../../services/PackageService';
import { getReplacedValue } from '../../../services/PlaceholderService';
import styles from './PageTemplate.module.scss';

export interface PageTemplateProps {
	stepBlock?: StepBlock;
	activePage?: number;
}
export const PageTemplate: React.FC<PageTemplateProps> = memo(({ stepBlock, activePage }) => {
	const { title, fields, contentId: stepContentId, errorMessage, campaigns } = stepBlock || {};

	const methods = useForm({ mode: 'onTouched' });
	const {
		handleSubmit,
		formState: { errors, isValid },
		register,
		resetField,
		setError,
		watch,
		trigger,
		setValue,
	} = methods;

	const { onSubmit } = useFormSubmit();

	const { isVisible, setIsVisible } = useContext(IsPackageComponentContext);

	const modal = useAppSelector((state) => state.modal);

	const isPackages = isPackageStep(stepBlock);

	const { isMobile } = useDeviceQuery();

	const getFormProperties = (name?: string) => {
		return {
			stepTitle: getReplacedValue(title),
			hasError: name ? !!errors?.[name] : false,
			errors,
			errorMessage,
			register,
			resetField,
			setError,
			isValid,
			watch,
			trigger,
			setValue,
			ctaSubmitStep: () => {
				handleSubmit(onSubmit)();
			},
			// insert this if field onchange should submit form
			// submitStep: () => handleSubmit(onSubmit)(),
		};
	};

	const mapComponents = () => {
		return fields.items.map((contentItem, index) => {
			const name = contentItem.content.uniqueIdentifier?.uid ?? 'NO_UNIQUE_ID_FOR_CONTENT_ITEM';

			const updatedComponentProps = getComponentProperties(
				stepContentId,
				contentItem,
				index,
				getFormProperties(name)
			);

			return updatedComponentProps ? (
				<Component
					key={contentItem.content.contentId}
					{...updatedComponentProps}
				/>
			) : undefined;
		});
	};

	const loginButtonUi = () => {
		const campaign: any = campaigns.items.find((campaign: any) => campaign.content?.contentType === 'ButtonBlock');
		if (campaign) {
			return (
				<Container
					mobileWidth={isPackages && isMobile ? '342' : 'fullWidth'}
					tabletWidth='996'
					desktopWidth='996'
					noPadding
				>
					<LoginButtonComponent
						key={campaign.content.contentId}
						onClick={() => {
							location.href = campaign.content.linkButtonSettings?.url ?? '';
						}}
					>
						{campaign.content.linkButtonSettings?.title ?? campaign.content.linkButtonSettings?.text}
					</LoginButtonComponent>
				</Container>
			);
		}
	};

	const pageTemplateRef = useRef<HTMLDivElement>(null);

	// Scroll to top when on next page when on mobile
	useEffect(() => {
		if (pageTemplateRef.current && pageTemplateRef.current.scrollTop > 0) {
			pageTemplateRef.current.scrollTo({ top: 0, behavior: 'smooth' });
		}
	}, [activePage]);

	useEffect(() => {
		setTimeout(() => {
			setIsVisible(isPackages);
		}, 100);
	}, []);

	return (
		<div
			ref={pageTemplateRef}
			className={classNames(styles.PageTemplate, isVisible && styles['PageTemplate--full-width'])}
		>
			<FormProvider {...methods}>
				<FormWrapper
					onSubmit={handleSubmit(onSubmit)}
					className={styles.PageTemplate__Form}
				>
					<StepComponent
						fields={fields}
						resetField={resetField}
					>
						<fieldset style={{ border: 'none', padding: 0, margin: 0 }}>
							{loginButtonUi()}
							{(!isPackages || (isPackages && !isMobile)) && (
								<Container
									mobileWidth={isPackages && !isMobile ? '342' : 'fullWidth'}
									tabletWidth={isPackages && !isMobile ? '996' : 'fullWidth'}
									desktopWidth={isPackages && !isMobile ? '996' : 'fullWidth'}
									noPadding={isPackages && !isMobile}
								>
									<legend className={styles.PageTemplate__Form__Headline}>
										<Headline
											title={getReplacedValue(title)}
											size='default'
											variant='h3'
											fontColor={isPackages ? 'white' : 'primary'}
										/>
									</legend>
								</Container>
							)}
							<div
								className={classNames(
									styles.PageTemplate__Form__Content
									//TODO: Remove comment if business wants center style again on package step
									//isVisible && styles['PageTemplate__Form__Content--package']
								)}
							>
								{mapComponents()}
							</div>
						</fieldset>
					</StepComponent>

					{modal.displayModal && <ModalComponent />}
				</FormWrapper>
			</FormProvider>
		</div>
	);
});

PageTemplate.displayName = 'PageTemplate';
